import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import TenantAudit from "./TenantAudit";
import { api, formatApiError } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { useAuth } from "@/contexts/AuthContext";
import IntegrationsTab from "@/pages/IntegrationsTab";
import LandingEditorTab from "@/pages/LandingEditorTab";
import FileUploader, { fullUrl } from "@/components/FileUploader";
import TenantAccessLinks from "@/components/TenantAccessLinks";

// ---------- Helpers de cor (HSL <-> HEX) ----------
function hslToHex(hslStr) {
  // "221 83% 53%" -> "#3b82f6"
  if (!hslStr) return "#3b82f6";
  const m = String(hslStr).match(/(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)%\s+(-?\d+(?:\.\d+)?)%/);
  if (!m) return "#3b82f6";
  const h = parseFloat(m[1]) / 360;
  const s = parseFloat(m[2]) / 100;
  const l = parseFloat(m[3]) / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h * 12) % 12;
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(255 * c).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function hexToHsl(hex) {
  // "#3b82f6" -> "221 83% 53%"
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return "221 83% 53%";
  let r = parseInt(m[1], 16) / 255;
  let g = parseInt(m[2], 16) / 255;
  let b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Recursos que realmente aparecem no menu para Pet/Barbearia/Beleza.
// Removidos: crm, funil_comercial, automacoes, servicos (fundido em agenda),
// vendas/pacotes/assinaturas, whatsapp (feature flag legado), relatorios, comunica_plus.
const ALL_MODULES = [
  "dashboard", "clientes", "agenda", "financeiro", "campanhas",
  "pesquisa_satisfacao", "fidelidade", "clube_beneficios",
  "indicacao_premiada", "configuracoes",
];

const MODULE_LABEL = {
  dashboard: "Visão Geral",
  clientes: "Clientes",
  agenda: "Agenda",
  financeiro: "Financeiro",
  campanhas: "Mensagens & Campanhas",
  pesquisa_satisfacao: "Pesquisa de Satisfação",
  fidelidade: "Programa de Fidelidade",
  clube_beneficios: "Clube de Benefícios",
  indicacao_premiada: "Indicações",
  configuracoes: "Configurações",
};

export default function TenantSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const allowedTabs = ["branding", "hours", "access", "modules", "fields", "integrations", "landing", "history"];
  const urlTab = searchParams.get("tab");
  // Compat: se alguém acessar ?tab=whatsapp (link antigo/histórico), cai em integrações.
  // Compat: ?tab=retention (Régua de Retorno removida — função coberta pelo Gestor de Risco).
  const activeTab = (urlTab === "whatsapp" || urlTab === "retention")
    ? "integrations"
    : (allowedTabs.includes(urlTab) ? urlTab : "branding");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/tenant/settings"); setData(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="p-10 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Configurações</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Personalize sua empresa</h1>
        <p className="text-sm text-muted-foreground mt-1">Identidade visual, recursos ativos e campos personalizados.</p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => {
        const next = new URLSearchParams(searchParams);
        next.set("tab", v);
        setSearchParams(next, { replace: true });
      }}>
        <TabsList>
          <TabsTrigger data-testid={TID.settingsTabBranding} value="branding">Identidade visual</TabsTrigger>
          <TabsTrigger data-testid="settings-tab-hours" value="hours">Funcionamento</TabsTrigger>
          <TabsTrigger data-testid="settings-tab-access" value="access">Acessos</TabsTrigger>
          <TabsTrigger data-testid={TID.settingsTabModules} value="modules">Recursos</TabsTrigger>
          <TabsTrigger data-testid={TID.settingsTabFields} value="fields">Campos</TabsTrigger>
          <TabsTrigger data-testid="settings-tab-integrations" value="integrations">Integrações</TabsTrigger>
          <TabsTrigger data-testid="settings-tab-landing" value="landing">Landing pública</TabsTrigger>
          <TabsTrigger data-testid="settings-tab-history" value="history">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="branding" className="mt-6">
          <BrandingForm initial={data.branding} onSaved={load} />
        </TabsContent>
        <TabsContent value="hours" className="mt-6">
          <BusinessHoursForm initial={data.settings?.business_hours} onSaved={load} />
        </TabsContent>
        <TabsContent value="access" className="mt-6">
          <Card className="p-6 rounded-tenant border-border">
            <h2 className="font-display text-xl font-semibold mb-1">Links de acesso da empresa</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Use estes links para entrar com seu usuário admin, dar acesso aos funcionários,
              compartilhar agendamento online com clientes e divulgar nas redes sociais.
            </p>
            <TenantAccessLinks
              slug={auth?.tenant?.slug}
              admin_email={auth?.user?.email}
              legalName={auth?.tenant?.legal_name}
              tenantId={auth?.tenant?.id}
              canResetAdmin={false}
            />
          </Card>
        </TabsContent>
        <TabsContent value="modules" className="mt-6">
          <ModulesForm initial={data.feature_flags} onSaved={load} />
        </TabsContent>
        <TabsContent value="fields" className="mt-6">
          <FieldsForm initial={data.custom_fields} onSaved={load} />
        </TabsContent>
        <TabsContent value="integrations" className="mt-6">
          <IntegrationsTab />
        </TabsContent>
        <TabsContent value="landing" className="mt-6">
          <LandingEditorTab />
        </TabsContent>
        <TabsContent value="history" className="mt-6">
          <TenantAudit embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BrandingForm({ initial, onSaved }) {
  const [b, setB] = useState({ ...initial });
  const [busy, setBusy] = useState(false);
  const auth = useAuth();

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/tenant/branding", b);
      toast.success("Branding atualizado");
      // Refresh user context so layout reflects new branding
      await auth.fetchMe();
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 rounded-tenant border-border">
      <form onSubmit={save} className="grid md:grid-cols-2 gap-5">
        <div className="space-y-1.5 md:col-span-2">
          <Label>Nome de exibição (como aparecerá para seus clientes)</Label>
          <Input data-testid={TID.brandingDisplayName} value={b.display_name || ""}
            onChange={(e) => setB({ ...b, display_name: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Site próprio (opcional)</Label>
          <Input
            data-testid="branding-website-url"
            placeholder="Ex.: tombarber.com.br"
            value={b.website_url || ""}
            onChange={(e) => setB({ ...b, website_url: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            URL externa da empresa. Aparecerá no seu site público.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Categoria / Slogan curto (opcional)</Label>
          <Input
            data-testid="branding-tagline"
            placeholder="Ex.: Barbearia · Salão · Pet Shop"
            value={b.tagline || ""}
            onChange={(e) => setB({ ...b, tagline: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            Aparece embaixo do nome no site público (formato: <em>&ldquo;Nome — Categoria&rdquo;</em>).
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Logo (JPEG)</Label>
          <FileUploader
            testid="branding-logo"
            value={b.logo_url || ""}
            onChange={(url) => setB({ ...b, logo_url: url })}
            accept="image/jpeg"
          />
          {b.logo_url && (
            <img src={fullUrl(b.logo_url)} alt="logo" className="mt-2 h-10 object-contain" onError={(e) => e.target.style.display = "none"} />
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Favicon (JPEG)</Label>
          <FileUploader
            testid="branding-favicon"
            value={b.favicon_url || ""}
            onChange={(url) => setB({ ...b, favicon_url: url })}
            accept="image/jpeg"
          />
          {b.favicon_url && (
            <img src={fullUrl(b.favicon_url)} alt="favicon" className="mt-2 h-8 w-8 object-contain border rounded" onError={(e) => e.target.style.display = "none"} />
          )}
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label>Cor principal da empresa</Label>
          <div className="flex flex-wrap items-center gap-3" data-testid="branding-color-picker-row">
            <input
              type="color"
              data-testid="branding-color-native"
              className="w-14 h-12 rounded-md border border-border cursor-pointer"
              value={hslToHex(b.primary_color || "221 83% 53%")}
              onChange={(e) => {
                const hsl = hexToHsl(e.target.value);
                setB({ ...b, primary_color: hsl });
              }}
            />
            <div className="flex-1 min-w-[180px]">
              <Input data-testid={TID.brandingPrimaryColor} value={b.primary_color || ""}
                onChange={(e) => setB({ ...b, primary_color: e.target.value })}
                placeholder="221 83% 53%"
                className="font-mono text-sm" />
              <p className="text-[10px] text-muted-foreground mt-1">Use o seletor ou digite no formato HSL (matiz saturação% luminosidade%).</p>
            </div>
            <div className="w-20 h-12 rounded-md border border-border shadow-inner" style={{ background: `hsl(${b.primary_color || "0 0% 50%"})` }} />
          </div>
          <div className="flex flex-wrap gap-1.5 pt-2">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground self-center mr-1">Presets:</span>
            {[
              ["Azul royal", "221 83% 53%"], ["Rosa", "340 82% 52%"], ["Vermelho", "0 84% 60%"],
              ["Laranja", "25 95% 53%"], ["Âmbar", "38 92% 50%"], ["Verde", "142 71% 45%"],
              ["Teal", "173 80% 40%"], ["Roxo", "262 83% 58%"], ["Preto", "0 0% 12%"],
            ].map(([name, hsl]) => (
              <button key={hsl} type="button"
                onClick={() => setB({ ...b, primary_color: hsl })}
                title={name}
                className="w-7 h-7 rounded-md border border-border hover:scale-110 transition shadow-sm"
                style={{ background: `hsl(${hsl})` }}
                data-testid={`branding-preset-${hsl.split(" ")[0]}`} />
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Cor do texto sobre a cor principal</Label>
          <Input value={b.primary_foreground || ""} onChange={(e) => setB({ ...b, primary_foreground: e.target.value })} placeholder="210 40% 98%" />
          <p className="text-[10px] text-muted-foreground">Use 210 40% 98% (quase branco) para fundos escuros, ou 0 0% 10% para fundos claros.</p>
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label>Título da tela de login</Label>
          <Input data-testid={TID.brandingLoginHeadline} value={b.login_headline || ""}
            onChange={(e) => setB({ ...b, login_headline: e.target.value })} />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label>Subtítulo da tela de login</Label>
          <Input value={b.login_subheadline || ""}
            onChange={(e) => setB({ ...b, login_subheadline: e.target.value })} />
        </div>
        <div className="md:col-span-2 flex justify-end">
          <Button type="submit" disabled={busy} data-testid={TID.brandingSubmit} className="gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar identidade visual
          </Button>
        </div>
      </form>
    </Card>
  );
}


const DAY_NAMES = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
// Default único — espelha /app/backend/core/business_hours.py:DEFAULT_HOURS.
// Qualquer mudança aqui deve ser feita também lá (e vice-versa).
const DEFAULT_HOURS_UI = {
  "0": null, "1": [9, 19], "2": [9, 19], "3": [9, 19],
  "4": [9, 19], "5": [9, 19], "6": [9, 19],
};

function BusinessHoursForm({ initial, onSaved }) {
  // Estado local: { "0": null | [open, close], ..., "6": ... }
  const seed = { ...DEFAULT_HOURS_UI, ...(initial || {}) };
  const [hours, setHours] = useState(seed);
  const [busy, setBusy] = useState(false);

  const toggleDay = (day) => {
    setHours((h) => ({ ...h, [day]: h[day] === null ? [9, 19] : null }));
  };
  const setOpen = (day, val) => {
    const parsed = Math.max(0, Math.min(23, parseInt(val, 10) || 0));
    setHours((h) => {
      const cur = h[day] || [9, 18];
      const close = cur[1] > parsed ? cur[1] : Math.min(24, parsed + 1);
      return { ...h, [day]: [parsed, close] };
    });
  };
  const setClose = (day, val) => {
    const parsed = Math.max(1, Math.min(24, parseInt(val, 10) || 0));
    setHours((h) => {
      const cur = h[day] || [9, 18];
      const open = cur[0] < parsed ? cur[0] : Math.max(0, parsed - 1);
      return { ...h, [day]: [open, parsed] };
    });
  };
  const copyMondayToWeekdays = () => {
    const mon = hours["1"];
    if (!mon) { toast.warning("Configure a segunda-feira primeiro."); return; }
    setHours((h) => ({ ...h, "2": [...mon], "3": [...mon], "4": [...mon], "5": [...mon] }));
    toast.success("Copiado para terça a sexta.");
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/tenant/settings/business-hours", { hours });
      toast.success("Horário de funcionamento salvo!");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 rounded-tenant border-border" data-testid="business-hours-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h3 className="font-display font-semibold text-lg">Horário de funcionamento</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Define os dias e horários em que a agenda aceita novos atendimentos.
            Fora desses horários o sistema bloqueia — sem risco de agendar às 7h se você abre às 9h.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={copyMondayToWeekdays}
          data-testid="hours-copy-weekdays">
          Copiar segunda → terça-sexta
        </Button>
      </div>

      <div className="space-y-2">
        {DAY_NAMES.map((name, idx) => {
          const key = String(idx);
          const h = hours[key];
          const isOpen = h !== null && Array.isArray(h);
          return (
            <div key={key} className="grid grid-cols-[90px_1fr] sm:grid-cols-[110px_90px_1fr] items-center gap-x-3 gap-y-2 py-2 border-b border-border/60 last:border-0"
              data-testid={`hours-row-${idx}`}>
              <div className="font-medium text-sm">{name}</div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <Switch checked={isOpen} onCheckedChange={() => toggleDay(key)}
                  data-testid={`hours-toggle-${idx}`} />
                <span className={isOpen ? "text-foreground" : "text-muted-foreground"}>
                  {isOpen ? "Aberto" : "Fechado"}
                </span>
              </label>
              {isOpen ? (
                <div className="col-span-2 sm:col-span-1 flex items-center gap-2 text-sm flex-wrap">
                  <Label className="text-xs text-muted-foreground">Abre</Label>
                  <Input
                    type="number" min="0" max="23"
                    value={h[0]}
                    onChange={(e) => setOpen(key, e.target.value)}
                    className="w-16 text-center"
                    data-testid={`hours-open-${idx}`}
                  />
                  <span className="text-muted-foreground">h</span>
                  <span className="mx-1 text-muted-foreground">até</span>
                  <Label className="text-xs text-muted-foreground">Fecha</Label>
                  <Input
                    type="number" min="1" max="24"
                    value={h[1]}
                    onChange={(e) => setClose(key, e.target.value)}
                    className="w-16 text-center"
                    data-testid={`hours-close-${idx}`}
                  />
                  <span className="text-muted-foreground">h</span>
                </div>
              ) : (
                <div className="col-span-2 sm:col-span-1 text-xs text-muted-foreground italic">Empresa fechada</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex justify-end">
        <Button onClick={save} disabled={busy} className="gap-2" data-testid="hours-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar horários
        </Button>
      </div>
    </Card>
  );
}

function ModulesForm({ initial, onSaved }) {
  const [flags, setFlags] = useState({ ...initial });
  const [busy, setBusy] = useState(false);

  const toggle = (k) => setFlags({ ...flags, [k]: !flags[k] });

  const save = async () => {
    setBusy(true);
    try {
      // ensure all known modules have a value
      const payload = {};
      ALL_MODULES.forEach((m) => { payload[m] = !!flags[m]; });
      await api.put("/tenant/feature-flags", payload);
      toast.success("Recursos atualizados");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 rounded-tenant border-border">
      <div className="grid sm:grid-cols-2 gap-3">
        {ALL_MODULES.map((m) => (
          <label key={m} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border border-border bg-card">
            <span className="text-sm font-medium">{MODULE_LABEL[m]}</span>
            <Switch data-testid={TID.moduleToggle(m)} checked={!!flags[m]} onCheckedChange={() => toggle(m)} />
          </label>
        ))}
      </div>
      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={busy} data-testid={TID.modulesSubmit} className="gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar módulos
        </Button>
      </div>
    </Card>
  );
}

function FieldsForm({ initial, onSaved }) {
  const [fields, setFields] = useState(initial || []);
  const [form, setForm] = useState({ resource: "client", key: "", label: "", field_type: "text" });
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/tenant/custom-fields", form);
      setFields([...fields, data]);
      setForm({ resource: form.resource, key: "", label: "", field_type: "text" });
      toast.success("Campo criado");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/tenant/custom-fields/${id}`);
      setFields(fields.filter((f) => f.id !== id));
      toast.success("Campo removido");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 rounded-tenant border-border">
        <div className="font-display text-lg font-semibold mb-4">Adicionar campo personalizado</div>
        <form onSubmit={add} className="grid md:grid-cols-5 gap-3">
          <div className="space-y-1.5 md:col-span-1">
            <Label>Recurso</Label>
            <Select value={form.resource} onValueChange={(v) => setForm({ ...form, resource: v })}>
              <SelectTrigger data-testid={TID.fieldResource}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="client">Cliente</SelectItem>
                <SelectItem value="deal">Oportunidade</SelectItem>
                <SelectItem value="service">Serviço</SelectItem>
                <SelectItem value="sale">Venda</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-1">
            <Label>Chave</Label>
            <Input data-testid={TID.fieldKey} required value={form.key}
              onChange={(e) => setForm({ ...form, key: e.target.value.replace(/\s+/g, "_").toLowerCase() })}
              placeholder="cpf" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Rótulo</Label>
            <Input data-testid={TID.fieldLabel} required value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="CPF" />
          </div>
          <div className="space-y-1.5 md:col-span-1">
            <Label>Tipo</Label>
            <Select value={form.field_type} onValueChange={(v) => setForm({ ...form, field_type: v })}>
              <SelectTrigger data-testid={TID.fieldType}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Texto</SelectItem>
                <SelectItem value="number">Número</SelectItem>
                <SelectItem value="date">Data</SelectItem>
                <SelectItem value="boolean">Sim/Não</SelectItem>
                <SelectItem value="select">Seleção</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-5 flex justify-end">
            <Button type="submit" disabled={busy} data-testid={TID.fieldAdd} className="gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Adicionar campo
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-6 rounded-tenant border-border">
        <div className="font-display text-lg font-semibold mb-4">Campos existentes ({fields.length})</div>
        {fields.length === 0 ? (
          <div className="text-sm text-muted-foreground">Nenhum campo personalizado ainda.</div>
        ) : (
          <ul className="divide-y divide-border">
            {fields.map((f) => (
              <li key={f.id} className="py-3 flex items-center justify-between gap-3 text-sm">
                <div>
                  <div className="font-medium">{f.label} <span className="text-muted-foreground font-mono text-xs">({f.key})</span></div>
                  <div className="text-xs text-muted-foreground">{f.resource} · {f.field_type}</div>
                </div>
                <Button size="sm" variant="ghost" className="text-destructive" data-testid={TID.fieldRemove(f.id)} onClick={() => del(f.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
